import { Card, CardContent } from './ui/card';
import { TrendingUp, AlertTriangle, Shield, Zap, Activity, Info } from 'lucide-react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './ui/popover';

interface RiskScoreData {
  overallScore?: number;
  vulnerabilityScore?: number;
  licenseScore?: number;
  licenseScoreDetails?: any;
  operationalRisk?: number;
  technicalDebt?: number;
  licenseIssues?: number;
}

interface RiskAssessmentScoresProps {
  riskScores: RiskScoreData;
  compact?: boolean;
  showLabels?: boolean;
}

export function RiskAssessmentScores({
  riskScores,
  compact = false,
  showLabels = true
}: RiskAssessmentScoresProps) {

  const getScoreColor = (score: number, type: 'overall' | 'vulnerability' | 'license' | 'operational' | 'debt') => {
 

    if (score >= 90) return 'text-red-900 dark:text-red-300';
    if (score >= 70) return 'text-red-600 dark:text-red-400';
    if (score >= 40) return 'text-orange-600 dark:text-orange-400';
    return 'text-green-600 dark:text-green-400';
  };

  const getCardColor = (score: number, type: 'overall' | 'vulnerability' | 'license' | 'operational' | 'debt') => {


    if (score >= 90) return 'bg-red-100 border-red-300 dark:bg-red-900/40 dark:border-red-700';
    if (score >= 70) return 'bg-red-50 border-red-200 dark:bg-red-950/30 dark:border-red-800';
    if (score >= 40) return 'bg-orange-50 border-orange-200 dark:bg-orange-950/30 dark:border-orange-800';
    return 'bg-green-50 border-green-200 dark:bg-green-950/30 dark:border-green-800';
  };

  const getIcon = (type: 'overall' | 'vulnerability' | 'license' | 'operational' | 'debt') => {
    switch (type) {
      case 'overall': return <TrendingUp className="w-4 h-4" />;
      case 'vulnerability': return <AlertTriangle className="w-4 h-4" />;
      case 'license': return <Shield className="w-4 h-4" />;
      case 'operational': return <Activity className="w-4 h-4" />;
      case 'debt': return <Zap className="w-4 h-4" />;
    }
  };

  const formatScore = (score: number, type: 'overall' | 'vulnerability' | 'license' | 'operational' | 'debt') => {

    return score?.toFixed(2);
  };

  const getLabel = (type: 'overall' | 'vulnerability' | 'license' | 'operational' | 'debt') => {
    switch (type) {
      case 'overall': return 'Overall Score';
      case 'vulnerability': return 'Vulnerability';
      case 'license': return 'License Issues';
      case 'operational': return 'Operational Risk';
      case 'debt': return 'Technical Debt';
    }
  };

  const getUnit = (type: 'overall' | 'vulnerability' | 'license' | 'operational' | 'debt') => {
   
    return '/100';
  };

  const scoreItems = [
    {
      type: 'overall' as const,
      score: riskScores.overallScore || 0,
      icon: getIcon('overall'),
      label: getLabel('overall'),
      unit: getUnit('overall')
    },
    {
      type: 'vulnerability' as const,
      score: riskScores.vulnerabilityScore || 0,
      icon: getIcon('vulnerability'),
      label: getLabel('vulnerability'),
      unit: getUnit('vulnerability')
    },
    {
      type: 'license' as const,
      score: riskScores.licenseScore || 0,
      icon: getIcon('license'),
      label: getLabel('license'),
      unit: getUnit('license')
    },
    {
      type: 'operational' as const,
      score: riskScores.operationalRisk || 0,
      icon: getIcon('operational'),
      label: getLabel('operational'),
      unit: getUnit('operational')
    },
    {
      type: 'debt' as const,
      score: riskScores.technicalDebt || 0,
      icon: getIcon('debt'),
      label: getLabel('debt'),
      unit: getUnit('debt')
    }
  ];

  const ScoreCard = ({ item }: { item: typeof scoreItems[0] }) => {

    return (
      <Popover>
        <PopoverTrigger asChild>
          <div className={`${getCardColor(item.score, item.type)} hover:shadow-md transition-shadow cursor-pointer rounded-lg border`}>
            <Card className="border-0 bg-transparent">
              <CardContent className="p-1 text-center" style={{paddingBottom: showLabels ? '0.5rem' : '0.75rem'}}>
                
                <div className={`text-lg font-bold ${getScoreColor(item.score, item.type)}`}>
                  {formatScore(item.score, item.type)}
                </div>
                {showLabels && (
                  <>
                    <div className="text-tiny font-medium text-foreground ">
                      {item.label}
                    </div>
                   
                  </>
                )}
              </CardContent>
            </Card>
          </div>
        </PopoverTrigger>
       
      </Popover>
    );
  };

  if (compact) {
    return (
      <div className="flex flex-wrap gap-2">
        {scoreItems.map((item) => (
          <div
            key={item.type}
            className={`flex flex-1 justify-center gap-1 px-2 py-1 rounded-md border ${getCardColor(item.score, item.type)}`}
          >
            <Popover>
              <PopoverTrigger asChild>
                <div className="flex items-center gap-1">
                 
                  <span className={`text-xs font-medium ${getScoreColor(item.score, item.type)}`}>
                    {formatScore(item.score, item.type)}
                  </span>
                
                </div>
              </PopoverTrigger>
              {/* <PopoverContent className="w-80 p-4">
                <div className="space-y-3">
                  <div>
                    <h4 className="font-semibold text-sm text-foreground mb-1">{getScoreExplanation(item.type).title}</h4>
                    <p className="text-xs text-muted-foreground">{getScoreExplanation(item.type).description}</p>
                  </div>

                  <div>
                    <p className="text-xs font-medium text-foreground mb-2">Range: {getScoreExplanation(item.type).range}</p>
                    <div className="space-y-1">
                      {getScoreExplanation(item.type).levels.map((level, index) => (
                        <div key={index} className="flex items-center justify-between text-xs">
                          <span className="font-medium">{level.range} - {level.level}</span>
                          <span className={level.color}>{level.meaning}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </PopoverContent> */}
            </Popover>
           
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {showLabels && (
        <p className="text-sm text-muted-foreground mb-2 font-medium">Risk Assessment Scores</p>
      )}

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2">
        {scoreItems.map((item) => (
          <ScoreCard key={item.type} item={item} />
        ))}
      </div>
    </div>
  );
}